<template>
  <v-container fluid class="productivity-v2-container pa-0 ma-0">
    <div class="productivity-v2-shell">
      <section v-if="showHeader" class="header-section productivity-v2-hero">
        <v-btn icon small class="header-close-btn" @click="dismissHeader">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
        <v-container fluid class="px-3">
          <v-row align="center" class="pa-0 ma-0">
            <v-col cols="12" md="8" class="d-flex align-center pa-0">
              <div class="header-icon-wrapper">
                <v-icon size="32" color="white">mdi-calendar-check</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="page-title">Productivity</h1>
                <p class="page-subtitle">
                  Review daily agent activity, office-time usage, call output, and idle time in one timeline.
                </p>
              </div>
            </v-col>
            <v-col cols="12" md="4" class="text-right pa-0 productivity-hero-chips">
              <v-chip class="ma-1 feature-chip" outlined>
                <v-icon left small>mdi-account-multiple</v-icon>
                Team Tracking
              </v-chip>
              <v-chip v-if="!$store.state.user.permissions.disableExportList" class="ma-1 feature-chip" outlined>
                <v-icon left small>mdi-file-export</v-icon>
                Export
              </v-chip>
            </v-col>
          </v-row>
          <v-row class="mt-4">
            <v-col cols="12" class="pa-0">
              <div class="feature-pills-container">
                <div class="feature-pill">
                  <v-icon small class="mr-2" color="white">mdi-clock-outline</v-icon>
                  Active Time
                </div>
                <div class="feature-pill">
                  <v-icon small class="mr-2" color="white">mdi-calendar-range</v-icon>
                  Date Range
                </div>
                <div class="feature-pill">
                  <v-icon small class="mr-2" color="white">mdi-account-search</v-icon>
                  Member Filter
                </div>
                <div class="feature-pill">
                  <v-icon small class="mr-2" color="white">mdi-phone-log</v-icon>
                  Call Tracking
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </section>

      <section class="productivity-v2-header">
        <div class="productivity-v2-summary">
          <p>Team: {{ selectedTeamLabel }} - {{ dateRangeSummary }}</p>
        </div>

        <div class="productivity-v2-actions">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="office-timing-btn"
                color="#199b38"
                outlined
                v-bind="attrs"
                @click="goToOfficeTiming"
                v-on="on"
              >
                <v-icon left small>mdi-clock-outline</v-icon>
                Set Office Timing
              </v-btn>
            </template>
            <span>Office timings are used to calculate attendance and productivity.</span>
          </v-tooltip>

          <v-btn
            v-if="!$store.state.user.permissions.disableExportList"
            class="export-report-btn"
            color="#37cf37"
            depressed
            :disabled="!visibleReports.length"
            @click="exportReport"
          >
            <v-icon left small>mdi-file-export</v-icon>
            Export
          </v-btn>
        </div>
      </section>

      <section class="filter-card">
        <div class="filter-version-toggle-row">
          <button
            type="button"
            class="classic-view-toggle classic-view-toggle-active productivity-filter-version-toggle"
            role="switch"
            aria-checked="true"
            @click="goToClassicProductivity"
          >
            <span class="classic-view-toggle-label classic-view-classic-label">Classic View</span>
            <span class="classic-view-switch" aria-hidden="true">
              <span class="classic-view-switch-thumb"></span>
            </span>
            <span class="classic-view-toggle-label classic-view-modern-label">Modern View</span>
          </button>
        </div>
        <v-row dense align="center">
          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="selectedUser"
              :items="allUsers"
              item-text="name"
              item-value="id"
              label="Members"
              hide-details
              outlined
              dense
              multiple
            >
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0">{{ item.name }}</span>
                <span v-if="index === 1" class="grey--text caption">
                  (+{{ selectedUser.length - 1 }} others)
                </span>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12" md="3">
            <v-dialog
              ref="dateDialog"
              v-model="modal"
              :return-value.sync="dates"
              persistent
              width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="dateRange"
                  label="Date Range"
                  prepend-inner-icon="event"
                  hide-details
                  outlined
                  dense
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="dates" color="primarygrad" scrollable range>
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="modal = false">Cancel</v-btn>
                <v-btn text color="primary" @click="$refs.dateDialog.save(dates)">
                  OK
                </v-btn>
              </v-date-picker>
            </v-dialog>
          </v-col>
          <v-col cols="12" md="2">
            <v-select
              v-model="sortBy"
              :items="sortOptions"
              item-text="text"
              item-value="value"
              label="Sort"
              hide-details
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-switch
              v-model="activeAgentsOnly"
              class="active-switch ma-0"
              color="#37cf37"
              hide-details
              label="Active agents only"
            />
          </v-col>
          <v-col cols="12" md="2" class="filter-actions">
            <v-btn color="#37cf37" depressed class="search-btn" @click="load">
              <v-icon left small>mdi-magnify</v-icon>
              Search
            </v-btn>
          </v-col>
        </v-row>
      </section>

      <section v-if="loading" class="loading-card">
        <p>{{ loadingText }}</p>
        <v-progress-linear color="#37cf37" indeterminate rounded height="4" />
      </section>

      <section v-else-if="visibleReports.length" class="agent-report-list">
        <article
          v-for="agent in visibleReports"
          :key="agent.id"
          class="agent-report-card"
        >
          <div class="agent-report-header">
            <div class="agent-identity">
              <v-avatar class="agent-avatar" size="44">
                <v-img
                  v-if="agent.pictureId"
                  :src="getImage(agent.picture)"
                  :alt="agent.name"
                />
                <span v-else>{{ getInitials(agent.name) }}</span>
              </v-avatar>
              <div>
                <h2>{{ agent.name }}</h2>
                <p>{{ agent.role || "Team member" }}</p>
              </div>
            </div>

            <div class="weekly-summary">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <p class="tooltip-label" v-bind="attrs" v-on="on">Weekly summary</p>
                </template>
                <span>Total calls, call duration, and productive percentage for this range.</span>
              </v-tooltip>
              <strong v-if="agent.summary.totalCalls">
                {{ agent.summary.totalCalls }} calls
                <span></span>
                {{ formatDuration(agent.summary.talkMinutes) }}
                <span></span>
                {{ formatPercent(agent.summary.productivePercent) }} productive
              </strong>
              <strong v-else class="muted-summary">No activity this week</strong>
            </div>
          </div>

          <div class="daily-grid">
            <div
              v-for="(day, index) in agent.days"
              :key="agent.id + '-' + day.dateKey"
              :class="['daily-card', { 'off-duty-card': day.count <= 0 }]"
            >
              <template v-if="day.count > 0">
                <v-tooltip v-if="isTopPerformer(agent.id, index, day.count)" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      class="top-performer-badge"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon size="13">mdi-trophy</v-icon>
                    </div>
                  </template>
                  <span>Top call count for this day.</span>
                </v-tooltip>

                <p class="day-label">{{ day.label }}</p>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      class="call-count-block tooltip-label"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <strong>{{ day.count }}</strong>
                      <span>calls</span>
                    </div>
                  </template>
                  <span>Total calls made by this agent on this day.</span>
                </v-tooltip>

                <div class="metric-list">
                  <div class="metric-row">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="tooltip-label" v-bind="attrs" v-on="on">
                          Duration
                        </span>
                      </template>
                      <span>Total call duration for this day.</span>
                    </v-tooltip>
                    <strong>{{ formatDuration(day.durationMinutes) }}</strong>
                  </div>
                  <div class="metric-row">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="tooltip-label" v-bind="attrs" v-on="on">
                          Connect%
                        </span>
                      </template>
                      <span>Connected calls divided by total calls.</span>
                    </v-tooltip>
                    <strong :class="getConnectionTone(day.connectionRate)">
                      {{ formatRate(day.connectionRate) }}
                    </strong>
                  </div>
                  <div class="metric-row">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="tooltip-label" v-bind="attrs" v-on="on">
                          Avg call
                        </span>
                      </template>
                      <span>Average duration per call.</span>
                    </v-tooltip>
                    <strong>{{ formatAverageCall(day) }}</strong>
                  </div>
                  <div class="metric-row">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="tooltip-label" v-bind="attrs" v-on="on">
                          Calls/hr
                        </span>
                      </template>
                      <span>Total calls divided by configured office hours.</span>
                    </v-tooltip>
                    <strong>{{ formatCallsPerHour(day.callsPerHour) }}</strong>
                  </div>
                </div>

                <div class="productive-block">
                  <div class="productive-heading">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span class="tooltip-label" v-bind="attrs" v-on="on">
                          Productive
                        </span>
                      </template>
                      <span>Call duration divided by configured office time.</span>
                    </v-tooltip>
                    <strong>{{ formatPercent(day.productivePercent) }}</strong>
                  </div>
                  <div class="productive-track">
                    <div
                      :class="['productive-fill', getProductiveTone(day.productivePercent)]"
                      :style="{ width: getProductiveWidth(day.productivePercent) }"
                    ></div>
                  </div>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <p class="tooltip-label" v-bind="attrs" v-on="on">
                        Idle: {{ formatDuration(day.idleMinutes) }} / On duty:
                        {{ formatDuration(day.onDutyMinutes) }}
                      </p>
                    </template>
                    <span>Idle time is office time minus call duration.</span>
                  </v-tooltip>
                </div>
              </template>

              <template v-else>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div class="off-duty-content" v-bind="attrs" v-on="on">
                      <v-icon class="off-duty-icon" size="24">mdi-calendar-remove</v-icon>
                      <p class="off-duty-day">{{ day.label }}</p>
                      <p class="off-duty-text">Off duty</p>
                    </div>
                  </template>
                  <span>No calls were recorded for this day.</span>
                </v-tooltip>
              </template>
            </div>
          </div>
        </article>
      </section>

      <section v-else class="empty-card">
        <v-img :src="summary" class="empty-image" contain />
        <h2>No productivity data found</h2>
        <p>Try changing the member filter or date range.</p>
      </section>

      <v-snackbar v-model="snackbar" multi-line :color="color">
        <h3>{{ snackbarText }}</h3>
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment-timezone";
import Papa from "papaparse";
import Blob from "blob";

export default {
  data() {
    return {
      activeAgentsOnly: false,
      allUsers: [],
      color: "primary",
      dates: [],
      loading: false,
      loadingText: "",
      modal: false,
      officeTime: 0,
      rangeOfDates: [],
      reports: [],
      selectedUser: [],
      showHeader: true,
      snackbar: false,
      snackbarText: "",
      sortBy: "name",
      sortOptions: [
        { text: "By name", value: "name" },
        { text: "By total calls", value: "totalCalls" },
        { text: "By connection rate %", value: "connectionRate" },
        { text: "By productive time %", value: "productivePercent" },
      ],
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "user", "teams", "selectedTeam"]),
    dateRange() {
      return this.sortedSelectedDates
        .map((date) => moment(date).format("DD-MM-YYYY"))
        .join(" - ");
    },
    dateRangeSummary() {
      if (!this.sortedSelectedDates.length) return "Select a date range";
      const start = moment(this.sortedSelectedDates[0]);
      const end = moment(this.sortedSelectedDates[1] || this.sortedSelectedDates[0]);
      if (start.isSame(end, "day")) return start.format("MMM D, YYYY");
      return `${start.format("MMM D")}-${end.format("MMM D, YYYY")}`;
    },
    selectedTeamLabel() {
      if (this.selectedTeam == "allTeams") return "All Teams";
      const team = this.teams.find((item) => item.id == this.selectedTeam);
      return team && team.name ? team.name : "Selected Team";
    },
    sortedSelectedDates() {
      if (!this.dates.length) return [];
      const start = moment(this.dates[0]);
      const end = this.dates[1] ? moment(this.dates[1]) : start.clone();
      if (start.isAfter(end)) return [end.format("YYYY-MM-DD"), start.format("YYYY-MM-DD")];
      return [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")];
    },
    summary() {
      return require("../assets/summary.png");
    },
    visibleReports() {
      let reports = this.reports.map((report) => this.enrichReport(report));

      if (this.activeAgentsOnly) {
        reports = reports.filter((report) => report.summary.totalCalls > 0);
      }

      return reports.sort((left, right) => {
        if (this.sortBy == "totalCalls") {
          return right.summary.totalCalls - left.summary.totalCalls;
        }
        if (this.sortBy == "connectionRate") {
          return right.summary.connectionRateValue - left.summary.connectionRateValue;
        }
        if (this.sortBy == "productivePercent") {
          return right.summary.productivePercent - left.summary.productivePercent;
        }
        return left.name.localeCompare(right.name);
      });
    },
  },
  watch: {
    async selectedTeam() {
      try {
        await this.fetchAccounts();
        await this.load();
      } catch (error) {
        this.showError("Error while loading productivity data");
      }
    },
  },
  async created() {
    this.showHeader = localStorage.getItem("productivityV2HeaderDismissed") !== "true";
    this.dates = [
      moment()
        .subtract(6, "days")
        .format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ];
    this.setOfficeTime();
    await this.fetchAccounts();
    await this.load();
  },
  methods: {
    buildDateRange() {
      const selectedDates = this.sortedSelectedDates;
      if (!selectedDates.length) return [];

      const start = moment(selectedDates[0]);
      const end = moment(selectedDates[1] || selectedDates[0]);
      const diff = end.diff(start, "days") + 1;
      const dates = [];

      for (let index = 0; index < diff; index++) {
        const current = end.clone().subtract(index, "days");
        dates.push({
          exportLabel: current.format("DD-MM-YYYY"),
          key: current.format("YYYY-MM-DD"),
          label: current.format("ddd D"),
        });
      }

      return dates;
    },
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("productivityV2HeaderDismissed", "true");
    },
    goToClassicProductivity() {
      this.$router.push({ path: "/attendance" });
    },
    enrichReport(report) {
      const days = report.data.map((day) => this.enrichDay(day));
      const activeDays = days.filter((day) => day.count > 0).length;
      const totalCalls = days.reduce((sum, day) => sum + day.count, 0);
      const totalConnected = days.reduce((sum, day) => sum + day.connectedCalls, 0);
      const talkMinutes = days.reduce((sum, day) => sum + day.durationMinutes, 0);
      const onDutyMinutes = activeDays * this.officeTime;
      const productivePercent = onDutyMinutes ? (talkMinutes / onDutyMinutes) * 100 : 0;
      const connectionRate = totalCalls ? (totalConnected / totalCalls) * 100 : null;

      return Object.assign({}, report, {
        days,
        summary: {
          connectionRate,
          connectionRateValue: connectionRate == null ? -1 : connectionRate,
          productivePercent,
          talkMinutes,
          totalCalls,
        },
      });
    },
    enrichDay(day) {
      const count = Number(day.count) || 0;
      const connectedCalls = Number(day.connectedCalls) || 0;
      const durationMinutes = Number(day.duration) || 0;
      const onDutyMinutes = count > 0 ? this.officeTime : 0;
      const connectionRate = count ? (connectedCalls / count) * 100 : null;
      const productivePercent = onDutyMinutes ? (durationMinutes / onDutyMinutes) * 100 : 0;

      return Object.assign({}, day, {
        callsPerHour: onDutyMinutes ? count / (onDutyMinutes / 60) : null,
        connectedCalls,
        connectionRate,
        count,
        durationMinutes,
        idleMinutes: Math.max(onDutyMinutes - durationMinutes, 0),
        onDutyMinutes,
        productivePercent,
      });
    },
    exportReport() {
      try {
        const exportData = [];
        this.visibleReports.forEach((agent) => {
          agent.days.forEach((day) => {
            exportData.push({
              Name: agent.name,
              Email: agent.email || "",
              Date: day.exportLabel,
              Calls: day.count,
              ConnectedCalls: day.connectedCalls,
              ConnectionRate: this.formatRate(day.connectionRate),
              DurationMinutes: Number(day.durationMinutes.toFixed(1)),
              AverageCallDuration: this.formatAverageCall(day),
              CallsPerHour: this.formatCallsPerHour(day.callsPerHour),
              ProductivePercent: this.formatPercent(day.productivePercent),
              IdleTime: this.formatDuration(day.idleMinutes),
              OnDutyTime: this.formatDuration(day.onDutyMinutes),
            });
          });
        });

        if (!exportData.length) {
          this.showWarning("No records to export");
          return;
        }

        const csv = Papa.unparse(exportData);
        const csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const csvUrl = navigator.msSaveBlob
          ? navigator.msSaveBlob(csvData, `productivity_v2_${new Date().getTime()}.csv`)
          : window.URL.createObjectURL(csvData);

        if (!navigator.msSaveBlob) {
          const tempLink = document.createElement("a");
          tempLink.href = csvUrl;
          tempLink.setAttribute(
            "download",
            `productivity_v2_${new Date().getTime()}.csv`,
          );
          tempLink.click();
        }
      } catch (error) {
        console.log(error);
        this.showError("Something went wrong while exporting reports.");
      }
    },
    async fetchAccounts() {
      try {
        const teams = this.getTeamIds();
        const response = await this.$http.post(`${this.ENDPOINT}/accounts/fetch-v2`, {
          teams,
        });
        const users = response.body || [];
        const canViewAll = this.user.role == "Manager" || this.user.role == "Teammanager";

        this.allUsers = canViewAll
          ? [{ id: "allMembers", name: "All" }].concat(users)
          : users;

        if (!this.selectedUser.length && this.allUsers.length) {
          this.selectedUser = [this.allUsers[0].id];
        }
      } catch (error) {
        const message = error.body && error.body.error && error.body.error.message;
        this.showError(message || "Error while fetching accounts");
      }
    },
    formatAverageCall(day) {
      if (!day.count) return "—";
      return this.formatDuration(day.durationMinutes / day.count);
    },
    formatCallsPerHour(value) {
      if (value == null) return "—";
      return Number(value).toFixed(1);
    },
    formatDuration(valueInMinutes) {
      const totalSeconds = Math.max(Math.round((Number(valueInMinutes) || 0) * 60), 0);
      if (!totalSeconds) return "0m";

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      if (hours && minutes) return `${hours}h ${minutes}m`;
      if (hours) return `${hours}h`;
      if (minutes) return `${minutes}m`;
      return `${seconds}s`;
    },
    formatPercent(value) {
      return `${Math.round(Number(value) || 0)}%`;
    },
    formatRate(value) {
      if (value == null) return "—";
      return this.formatPercent(value);
    },
    getConnectionTone(value) {
      if (value == null) return "metric-muted";
      if (value > 70) return "metric-success";
      if (value >= 50) return "metric-warning";
      return "metric-danger";
    },
    getImage(pic) {
      return `${this.ENDPOINT}/storages/get/${pic}?access_token=${this.$store.getters.user.token}`;
    },
    getInitials(name) {
      return (
        String(name || "")
          .split(" ")
          .filter((part) => part)
          .slice(0, 2)
          .map((part) => part.charAt(0).toUpperCase())
          .join("") || "--"
      );
    },
    getProductiveTone(value) {
      if (value > 20) return "productive-success";
      if (value >= 10) return "productive-warning";
      return "productive-danger";
    },
    getProductiveWidth(value) {
      const percent = Math.min(Math.max(Number(value) || 0, 0), 100);
      if (percent > 0 && percent < 1) return "1%";
      return `${percent}%`;
    },
    getTeamIds() {
      let ids = [];
      if (this.selectedTeam == "allTeams") {
        ids = this.teams.map((team) => team.id);
      } else {
        ids.push(this.selectedTeam);
      }
      return ids.filter((id) => id != "allTeams");
    },
    getUserById(id) {
      return this.allUsers.find((user) => user.id == id) || {};
    },
    getUserIds() {
      let users = [];
      if (this.selectedUser.indexOf("allMembers") > -1) {
        users = this.allUsers.map((item) => item.id);
      } else {
        users = this.selectedUser.slice();
      }
      return users.filter((id) => id != "allMembers");
    },
    goToOfficeTiming() {
      this.$router.push({ path: "/organization" });
    },
    isTopPerformer(agentId, dayIndex, count) {
      if (!count) return false;
      const maxForDay = Math.max.apply(
        null,
        this.reports.map((report) => Number(report.data[dayIndex].count) || 0),
      );
      return count == maxForDay && maxForDay > 0;
    },
    async load() {
      try {
        this.loading = true;
        this.loadingText = "Please wait, we are processing your data";
        const dateRange = this.buildDateRange();

        if (dateRange.length > 31) {
          this.showWarning("Please select a range of 30 days");
          return;
        }

        const users = this.getUserIds();
        if (!users.length) {
          this.reports = [];
          return;
        }

        const response = await this.$http.post(`${this.ENDPOINT}/logs/v2/filterByDays`, {
          users,
          dates: this.sortedSelectedDates,
        });
        const responseData = response.body || [];

        this.rangeOfDates = dateRange;
        this.reports = users.map((accountId) => {
          const user = this.getUserById(accountId);
          return {
            email: user.email || "",
            id: accountId,
            name: user.name || "Unknown member",
            picture: user.picture,
            pictureId: user.pictureId,
            role: user.role || "Team member",
            data: dateRange.map((date) => {
              const dayData = responseData.find((item) => {
                return (
                  item._id.accountsId == accountId &&
                  moment(item._id.day).format("YYYY-MM-DD") == date.key
                );
              });

              if (!dayData) {
                return Object.assign({}, date, {
                  connectedCalls: 0,
                  count: 0,
                  duration: 0,
                  timeSpentOnCall: 0,
                });
              }

              return Object.assign({}, date, {
                connectedCalls: Number(dayData.connectedCalls) || 0,
                count: Number(dayData.count) || 0,
                duration: Number(dayData.duration || 0) / 1000 / 60,
                timeSpentOnCall: Number(dayData.timeSpentOnCall || 0) / 1000 / 60,
              });
            }),
          };
        });
      } catch (error) {
        console.log("Error", error);
        this.showError("Error while loading your data");
      } finally {
        this.loading = false;
        this.loadingText = "";
      }
    },
    setOfficeTime() {
      const companyDetails = this.$store.state.companyDetails || {};
      if (!companyDetails.officeStartTime || !companyDetails.officeEndTime) {
        this.officeTime = 0;
        return;
      }

      const officeStart = this.$moment(companyDetails.officeStartTime, "HH:mm");
      const officeEnd = this.$moment(companyDetails.officeEndTime, "HH:mm");
      this.officeTime = Math.max(
        this.$moment.duration(officeEnd.diff(officeStart)).asMinutes(),
        0,
      );
    },
    showError(message) {
      this.snackbar = true;
      this.snackbarText = message;
      this.color = "error";
    },
    showWarning(message) {
      this.snackbar = true;
      this.snackbarText = message;
      this.color = "#cc9900";
    },
  },
};
</script>

<style scoped>
.productivity-v2-container {
  background: #f7f8f5;
  min-height: 100vh;
}

.productivity-v2-shell {
  padding: 16px;
}

.productivity-v2-hero {
  margin: 0 0 16px;
  position: relative;
}

.productivity-hero-chips {
  padding-right: 18px !important;
}

.productivity-v2-hero .feature-chip {
  border-color: rgba(255, 255, 255, 0.4) !important;
  color: #ffffff !important;
}

.productivity-v2-hero .feature-chip >>> .v-chip__content,
.productivity-v2-hero .feature-chip >>> .v-icon {
  color: #ffffff !important;
}

.productivity-v2-header {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr) auto;
  margin-bottom: 14px;
}

.productivity-v2-summary {
  min-width: 0;
}

.productivity-v2-header p {
  color: #555a54;
  font-size: 14px;
  line-height: 1.35;
  margin: 0;
}

.productivity-v2-actions {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  justify-content: flex-end;
  justify-self: end;
}

.office-timing-btn,
.export-report-btn,
.search-btn {
  border-radius: 7px;
  height: 40px !important;
  text-transform: none;
  white-space: nowrap;
}

.classic-view-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #333833;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 10px;
  height: 40px;
  justify-content: center;
  padding: 0 4px;
  white-space: nowrap;
}

.classic-view-toggle:focus-visible {
  border-radius: 999px;
  box-shadow: 0 0 0 3px rgba(25, 155, 56, 0.2);
  outline: none;
}

.classic-view-toggle:hover .classic-view-toggle-label {
  color: #199b38;
}

.classic-view-toggle-label {
  color: #6f746e;
  transition: color 0.18s ease;
}

.classic-view-classic-label {
  color: #199b38;
}

.classic-view-switch {
  background: #cfd4cd;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
  display: inline-flex;
  height: 20px;
  padding: 2px;
  transition: background 0.18s ease;
  width: 40px;
}

.classic-view-switch-thumb {
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.22);
  height: 16px;
  transition: transform 0.18s ease;
  width: 16px;
}

.classic-view-toggle-active .classic-view-classic-label {
  color: #6f746e;
}

.classic-view-toggle-active .classic-view-modern-label {
  color: #199b38;
}

.classic-view-toggle-active .classic-view-switch {
  background: #199b38;
}

.classic-view-toggle-active .classic-view-switch-thumb {
  transform: translateX(18px);
}

.export-report-btn,
.search-btn {
  color: #ffffff !important;
}

.filter-card,
.agent-report-card,
.loading-card,
.empty-card {
  background: #ffffff;
  border: 1px solid #deded9;
  border-radius: 10px;
}

.filter-card {
  margin-bottom: 16px;
  overflow: hidden;
  padding: 14px;
  position: relative;
}

.filter-card::before {
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  content: "";
  height: 4px;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
}

.filter-version-toggle-row {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
}

.active-switch >>> .v-label {
  color: #343733;
  font-size: 13px;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
}

.loading-card,
.empty-card {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 220px;
  padding: 28px;
}

.loading-card p,
.empty-card p {
  color: #555a54;
  font-size: 13px;
  margin: 0 0 12px;
}

.empty-card h2 {
  color: #151515;
  font-size: 18px;
  margin: 10px 0 4px;
}

.empty-image {
  max-width: 160px;
  opacity: 0.55;
}

.agent-report-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.agent-report-card {
  padding: 18px 20px 16px;
}

.agent-report-header {
  align-items: center;
  border-bottom: 1px solid #e4e4df;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
}

.agent-identity {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
}

.agent-avatar {
  background: #e5f1d6 !important;
  color: #2f6b16 !important;
  font-size: 14px;
  font-weight: 700;
}

.agent-identity h2 {
  color: #111111;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 2px;
}

.agent-identity p,
.weekly-summary p {
  color: #555a54;
  font-size: 13px;
  line-height: 1.35;
  margin: 0;
}

.weekly-summary {
  flex: 0 0 auto;
  text-align: right;
}

.weekly-summary strong {
  align-items: center;
  color: #111111;
  display: flex;
  flex-wrap: wrap;
  font-size: 17px;
  font-weight: 700;
  gap: 7px;
  justify-content: flex-end;
  line-height: 1.25;
  margin-top: 2px;
}

.weekly-summary strong span {
  background: #111111;
  border-radius: 50%;
  display: inline-block;
  height: 4px;
  width: 4px;
}

.weekly-summary .muted-summary {
  color: #777b74;
}

.tooltip-label {
  cursor: help;
}

.daily-grid {
  display: grid;
  gap: 12px;
  grid-auto-columns: minmax(170px, 1fr);
  grid-auto-flow: column;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
  scrollbar-width: thin;
}

.daily-grid::-webkit-scrollbar {
  height: 8px;
}

.daily-grid::-webkit-scrollbar-track {
  background: #eeeeea;
  border-radius: 99px;
}

.daily-grid::-webkit-scrollbar-thumb {
  background: #c8cbc3;
  border-radius: 99px;
}

.daily-card {
  background: #f3f3ef;
  border-radius: 8px;
  min-height: 240px;
  padding: 12px;
  position: relative;
}

.top-performer-badge {
  align-items: center;
  background: #f3e9d7;
  border-radius: 50%;
  color: #8a630e;
  display: flex;
  height: 22px;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 8px;
  width: 22px;
}

.day-label {
  color: #343733;
  font-size: 12px;
  font-weight: 600;
  margin: 0 0 8px;
}

.call-count-block {
  margin-bottom: 12px;
}

.call-count-block strong {
  color: #111111;
  display: block;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
}

.call-count-block span {
  color: #555a54;
  font-size: 11px;
}

.metric-list {
  border-top: 1px solid #d7d7d1;
  padding-top: 8px;
}

.metric-row,
.productive-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.metric-row {
  gap: 8px;
  margin-bottom: 4px;
}

.metric-row span,
.productive-heading span {
  color: #555a54;
  font-size: 11px;
}

.metric-row strong,
.productive-heading strong {
  color: #111111;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.metric-success {
  color: #1d9e75 !important;
}

.metric-warning {
  color: #b67810 !important;
}

.metric-danger {
  color: #b73535 !important;
}

.metric-muted {
  color: #777b74 !important;
}

.productive-block {
  margin-top: 8px;
}

.productive-track {
  background: #ddddda;
  border-radius: 99px;
  height: 4px;
  margin-top: 4px;
  overflow: hidden;
}

.productive-fill {
  border-radius: inherit;
  height: 100%;
}

.productive-success {
  background: #1d9e75;
}

.productive-warning {
  background: #ef9f27;
}

.productive-danger {
  background: #e05a5a;
}

.productive-block p {
  color: #777b74;
  font-size: 10px;
  line-height: 1.25;
  margin: 5px 0 0;
}

.off-duty-card {
  align-items: center;
  background: #f5f5f2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.off-duty-content {
  align-items: center;
  cursor: help;
  display: flex;
  flex-direction: column;
}

.off-duty-icon {
  color: #777b74;
  margin-bottom: 8px;
}

.off-duty-day,
.off-duty-text {
  color: #676b65;
  font-size: 12px;
  line-height: 1.3;
  margin: 0;
}

.off-duty-day {
  font-weight: 600;
}

.off-duty-text {
  margin-top: 2px;
}

.v-snack h3 {
  font-size: 13px;
  font-weight: 400;
  margin: 0;
  text-transform: uppercase;
}

.theme--dark .productivity-v2-container {
  background: #121212 !important;
}

.theme--dark .filter-card,
.theme--dark .agent-report-card,
.theme--dark .loading-card,
.theme--dark .empty-card {
  background: #1e1e1e;
  border-color: #333333;
}

.theme--dark .agent-identity h2,
.theme--dark .weekly-summary strong,
.theme--dark .call-count-block strong,
.theme--dark .metric-row strong,
.theme--dark .productive-heading strong,
.theme--dark .empty-card h2 {
  color: #f5f5f5;
}

.theme--dark .productivity-v2-header p,
.theme--dark .agent-identity p,
.theme--dark .weekly-summary p,
.theme--dark .call-count-block span,
.theme--dark .metric-row span,
.theme--dark .productive-heading span,
.theme--dark .productive-block p,
.theme--dark .loading-card p,
.theme--dark .empty-card p {
  color: #bdbdbd;
}

.theme--dark .daily-card,
.theme--dark .off-duty-card {
  background: #292929;
}

@media (max-width: 960px) {
  .productivity-v2-shell {
    padding: 12px;
  }

  .productivity-v2-header,
  .agent-report-header {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  }

  .productivity-v2-actions {
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .productivity-hero-chips {
    margin-top: 16px;
    padding-right: 0 !important;
    text-align: left !important;
  }

  .page-title {
    font-size: 22px;
  }

  .header-icon-wrapper {
    border-radius: 12px;
    height: 48px;
    width: 48px;
  }

  .header-icon-wrapper .v-icon {
    font-size: 24px !important;
  }

  .weekly-summary {
    text-align: left;
  }

  .weekly-summary strong {
    justify-content: flex-start;
  }

  .filter-actions {
    justify-content: flex-start;
  }

  .daily-card {
    min-height: 0;
  }
}
</style>